import { Dialog as RadixDialog, DropdownMenu } from 'radix-ui';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { cn } from '../../lib/cn';
import {
  Check,
  ChevronDown,
  ChevronUp,
  decorativeIcon,
  LogOut,
  Menu,
  X,
  type LucideIcon,
} from '../../lib/icons';
import { Avatar } from '../Avatar/Avatar';
import { Badge, type BadgeVariants } from '../Badge/Badge';
import { IconButton } from '../IconButton/IconButton';
import { Wordmark } from '../Wordmark/Wordmark';
import { useShellMode, type ShellMode } from './useShellMode';

export interface AdminNavItem {
  readonly key: string;
  /** Omit for a pure group header. Every navigable item is a real `<a href>`. */
  readonly href?: string;
  /** Already translated. Always rendered — never an icon-only nav. */
  readonly label: string;
  /** Parents carry one; sub-items carry none, by design. */
  readonly icon?: LucideIcon;
  /**
   * Exactly ONE item in the whole tree is current — the deepest match. A parent
   * whose child is active gets no indicator: being expanded is its signal.
   */
  readonly current?: boolean;
  /**
   * Rendered only when `count > 0`. `label` is the sentence a reader hears.
   *
   * `tone` defaults to the `Badge`'s own `info`: a count is inventory until
   * something says otherwise. Pass `danger` for the one count in the product
   * that means *act now* — the open high-severity exceptions.
   */
  readonly badge?: {
    readonly count: number;
    readonly label: string;
    readonly tone?: BadgeVariants['tone'];
  };
  readonly children?: readonly AdminNavItem[];
}

export interface AdminNavGroup {
  readonly key: string;
  readonly items: readonly AdminNavItem[];
  /** Draws a 1 px divider above this group — the elevated-permission block. */
  readonly separated?: boolean;
}

export interface AccountScope {
  readonly key: string;
  /** Already translated: the contract name, or "Tous les sites (3)". */
  readonly label: string;
  readonly current?: boolean;
}

/** What the shell hands a caller-supplied nav link besides its markup. */
export interface NavLinkContext {
  /**
   * Dismisses the ≤ 768 px drawer. The shell's own `<a>` calls it on click;
   * a router `<Link>` must call it too, or the drawer covers the page it
   * just navigated to.
   */
  readonly onNavigate: () => void;
  /**
   * The roving tab index for this item: the nav is a SINGLE tab stop, so
   * exactly one item in the tree carries `0` and every other one carries
   * `-1`. A router `<Link>` must spread this rather than deriving it from
   * `item.current`, because on a route the nav does not contain — `/profil`,
   * a 404 — NOTHING is current, and a tree of nothing but `-1` is a primary
   * navigation no keyboard can reach (WCAG 2.1.1). The shell falls the stop
   * back onto the first item for exactly that case.
   */
  readonly tabIndex: number;
}

export interface AccountMenuProps {
  readonly name: string;
  /** Override the derived initials where the locale's rules differ. */
  readonly initials?: string;
  /**
   * The caller's own photo (P-10), already resolved to an object URL — this
   * component never fetches one. Omitted (rather than `undefined` on every
   * caller) falls back to the initials tile, exactly like `Avatar` itself.
   */
  readonly photoSrc?: string;
  /**
   * "Superviseure · Olymel · 3 sites", already translated and assembled by
   * the caller: the role word from `user.role` (the one system-wide role), the
   * contract and site count from the current membership's scope.
   */
  readonly scopeLine: string;
  /** Accessible name for the trigger and the menu. Already translated. */
  readonly triggerLabel: string;
  readonly menuLabel: string;

  readonly scopeSectionLabel: string;
  readonly scopes: readonly AccountScope[];
  readonly onScopeChange?: (key: string) => void;

  readonly languageSectionLabel: string;
  readonly locale: string;
  /** `{ fr: 'Français', en: 'English' }`, already translated. */
  readonly localeOptions: readonly { readonly value: string; readonly label: string }[];
  readonly onLocaleChange: (locale: string) => void;

  /**
   * "Mon profil" (Phase 6). Both are optional and go together: the item
   * renders only when `onSelectProfile` is given, so a host with no profile
   * page yet keeps today's menu exactly as it is — additive, never a breaking
   * change to an existing caller. A callback like `onChangePin`, never an
   * href: an `<a>` here is a full document navigation, which drops the
   * in-memory access token and the whole query cache.
   */
  readonly onSelectProfile?: () => void;
  readonly profileLabel?: string;

  /** "Changer de NIP". A voluntary action, not only a forced one. */
  readonly changePinLabel: string;
  readonly onChangePin: () => void;
  readonly signOutLabel: string;
  readonly onSignOut: () => void;
}

export interface AppShellAdminProps {
  readonly brandLabel: string;
  readonly brandSrc?: string;
  /** "CONNECT · PORTAIL". Already translated. */
  readonly brandSubLabel?: string;
  /** Where the wordmark links. Omit on the landing route — it is not a link there. */
  readonly brandHref?: string;

  /**
   * Only the items the caller may see. An item the caller lacks permission for
   * is NOT rendered — never disabled. A greyed "Paramètres" tells a supervisor
   * the route exists and that they are not trusted with it, which contradicts
   * a backend whose out-of-scope reads answer 404 rather than 403.
   */
  readonly groups: readonly AdminNavGroup[];
  /** The nav's accessible name, already translated. */
  readonly navLabel: string;
  /** Accessible names for the drawer's controls. Already translated. */
  readonly menuLabel: string;
  readonly closeMenuLabel: string;
  /** "Aller au contenu" — the first focusable element on every page. */
  readonly skipLabel: string;
  /** Accessible name pattern for a group toggle: `(label) => "Ouvrir Horaires"`. */
  readonly groupToggleLabel?: (label: string) => string;

  readonly account: AccountMenuProps;

  /**
   * Lets an app inject its router's link. Defaults to a plain `<a>`.
   *
   * The fourth argument carries the shell's own `onNavigate`, which is what
   * closes the 280 px drawer. The built-in `<a>` gets it for free; a router
   * `<Link>` does not navigate through the shell at all, so without it the
   * drawer stays open over the page the reader just asked for — and an app
   * that works around it by remounting the shell on every pathname change
   * throws away the nav's scroll position and its open groups. Attach it as
   * the link's `onClick`.
   *
   * The parameter is optional and the fourth argument is additive: a
   * three-argument implementation keeps compiling and keeps behaving exactly
   * as it did.
   */
  readonly renderNavLink?: (
    item: AdminNavItem,
    className: string,
    children: ReactNode,
    context: NavLinkContext,
  ) => ReactNode;

  /** Forces a structure. For the gallery and for tests; unset in the app. */
  readonly mode?: ShellMode;

  readonly children: ReactNode;
  readonly className?: string;
}

function containsCurrent(item: AdminNavItem): boolean {
  if (item.current === true) return true;
  return (item.children ?? []).some(containsCurrent);
}

/**
 * The admin portal shell.
 *
 * Three zones and no global top bar: the page title, the context line and the
 * page actions live in a `PageHeader` inside the content column, so every route
 * owns its own header and nothing costs 56 px of vertical on a 768 px-tall
 * laptop where the punch grid needs every row.
 *
 * Three structures, chosen by width rather than by device:
 *
 * | width      | nav                                  |
 * |------------|--------------------------------------|
 * | <= 768     | 56 px top bar + 280 px drawer        |
 * | 769–1279   | 80 px LABELLED rail, click flyouts   |
 * | >= 1280    | 216 px grouped list, groups collapse |
 *
 * The rail is 80 px and never icon-only: *Organisation* measures ~66 px at the
 * 11 px label size, and an icon-only navigation is on this product's refusal
 * list. Its sub-menus open on CLICK, never on hover — a hover flyout puts
 * *Ajustements* out of reach of a keyboard entirely.
 *
 * Focus inside the chassis is `--color-focus-inverse` (brand blue, 6.78:1);
 * action blue measures 3.29:1 on `#14171A` and 2.62:1 on an active item, so the
 * default ring would be a ring nobody can see.
 */
export function AppShellAdmin({
  brandLabel,
  brandSrc,
  brandSubLabel,
  brandHref,
  groups,
  navLabel,
  menuLabel,
  closeMenuLabel,
  skipLabel,
  groupToggleLabel,
  account,
  renderNavLink,
  mode: modeOverride,
  children,
  className,
}: AppShellAdminProps) {
  const mode = useShellMode(modeOverride);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Navigating closes the drawer; so does growing past the breakpoint.
  useEffect(() => {
    if (mode !== 'drawer') setDrawerOpen(false);
  }, [mode]);

  const brand = (
    <Wordmark
      label={brandLabel}
      {...(brandSrc !== undefined ? { src: brandSrc } : {})}
      {...(brandSubLabel !== undefined ? { product: brandSubLabel } : {})}
      className="text-ink-inverse"
    />
  );

  const sidebarBody = (
    <>
      <div className="flex min-h-14 shrink-0 items-center px-4">
        {brandHref !== undefined ? (
          <a href={brandHref} className="rounded-control focus-visible:outline-focus-inverse">
            {brand}
          </a>
        ) : (
          brand
        )}
      </div>

      <AdminNav
        groups={groups}
        label={navLabel}
        mode={mode === 'drawer' ? 'drawer' : mode}
        {...(renderNavLink !== undefined ? { renderNavLink } : {})}
        {...(groupToggleLabel !== undefined ? { groupToggleLabel } : {})}
        onNavigate={() => setDrawerOpen(false)}
      />

      <div className="shrink-0 border-t border-hairline">
        <AccountMenu {...account} compact={mode === 'rail'} />
      </div>
    </>
  );

  return (
    <div className={cn('flex min-h-dvh w-full bg-canvas text-ink', className)}>
      <a
        href="#admin-main"
        className={cn(
          'sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-50',
          'focus:rounded-control focus:bg-surface focus:px-3 focus:py-2 focus:text-body',
        )}
      >
        {skipLabel}
      </a>

      {mode === 'drawer' ? (
        <RadixDialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
          <RadixDialog.Portal>
            <RadixDialog.Overlay className="fixed inset-0 z-40 bg-overlay" />
            <RadixDialog.Content
              aria-label={navLabel}
              className={cn(
                'fixed inset-y-0 start-0 z-50 flex w-sidebar-drawer flex-col',
                'border-e border-hairline bg-chassis text-ink-inverse',
                'focus:outline-none',
              )}
            >
              <div className="flex justify-end p-2">
                <RadixDialog.Close asChild>
                  <IconButton label={closeMenuLabel} variant="chassis" size="md">
                    <X {...decorativeIcon('md')} />
                  </IconButton>
                </RadixDialog.Close>
              </div>
              {sidebarBody}
            </RadixDialog.Content>
          </RadixDialog.Portal>
        </RadixDialog.Root>
      ) : (
        <div
          className={cn(
            'flex shrink-0 flex-col border-e border-hairline bg-chassis text-ink-inverse',
            'sticky top-0 h-dvh',
            mode === 'rail' ? 'w-sidebar-rail' : 'w-sidebar',
          )}
        >
          {sidebarBody}
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {mode === 'drawer' ? (
          <div className="flex min-h-14 shrink-0 items-center justify-between gap-2 bg-chassis px-3">
            <IconButton
              label={menuLabel}
              variant="chassis"
              size="lg"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
            >
              <Menu {...decorativeIcon('lg')} />
            </IconButton>
            <div className="min-w-0 text-ink-inverse">{brand}</div>
            <AccountMenu {...account} compact />
          </div>
        ) : null}

        <main
          id="admin-main"
          className={cn(
            'min-w-0 flex-1',
            mode === 'drawer' ? 'p-3' : mode === 'rail' ? 'p-5' : 'p-6',
          )}
        >
          <div className="mx-auto min-w-0 max-w-content-max">{children}</div>
        </main>
      </div>
    </div>
  );
}

interface AdminNavProps {
  readonly groups: readonly AdminNavGroup[];
  readonly label: string;
  readonly mode: ShellMode;
  readonly renderNavLink?: AppShellAdminProps['renderNavLink'];
  readonly groupToggleLabel?: (label: string) => string;
  readonly onNavigate: () => void;
}

function AdminNav({
  groups,
  label,
  mode,
  renderNavLink,
  groupToggleLabel,
  onNavigate,
}: AdminNavProps) {
  const nav = useRef<HTMLElement>(null);
  const rail = mode === 'rail';

  const activeGroups = useMemo(() => {
    const open = new Set<string>();
    for (const group of groups) {
      for (const item of group.items) {
        if (item.children !== undefined && containsCurrent(item)) open.add(item.key);
      }
    }
    return open;
  }, [groups]);

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(activeGroups);

  /**
   * The item that owns the single tab stop when no route matches: the first
   * one that renders something focusable. `undefined` while something IS
   * current, so the stop stays on the active item.
   */
  const fallbackKey = useMemo(() => {
    const anyCurrent = groups.some((group) => group.items.some(containsCurrent));
    if (anyCurrent) return undefined;
    for (const group of groups) {
      for (const item of group.items) {
        const focusable = item.href !== undefined || (item.children ?? []).length > 0;
        if (focusable) return item.key;
      }
    }
    return undefined;
  }, [groups]);

  useEffect(() => {
    // The group holding the active route is always expanded and cannot be
    // collapsed to nothing — otherwise the page you are on has no marker.
    setExpanded((previous) => {
      // Returning a fresh Set every time re-rendered the whole nav twice per
      // 30 s badge poll. Nothing added means nothing changed.
      const missing = [...activeGroups].filter((key) => !previous.has(key));
      return missing.length === 0 ? previous : new Set([...previous, ...missing]);
    });
  }, [activeGroups]);

  /**
   * The nav is a SINGLE tab stop that lands on the active item; arrows move
   * inside it. Tabbing through fourteen sub-labels to reach the content is not
   * navigation, it is a penalty for using a keyboard.
   */
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    const container = nav.current;
    if (container === null) return;
    const focusable = [...container.querySelectorAll<HTMLElement>('[data-nav-focusable]')];
    const index = focusable.indexOf(document.activeElement as HTMLElement);
    if (index === -1) return;

    let next = -1;
    if (event.key === 'ArrowDown') next = Math.min(index + 1, focusable.length - 1);
    else if (event.key === 'ArrowUp') next = Math.max(index - 1, 0);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = focusable.length - 1;

    if (next !== -1) {
      event.preventDefault();
      focusable[next]?.focus();
    }
  }, []);

  return (
    /* The handler implements the roving tabindex that makes the nav a single
     * tab stop. The interactive elements are the <a> and <button> children; the
     * listener only redirects focus between them. */
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <nav
      ref={nav}
      aria-label={label}
      onKeyDown={handleKeyDown}
      className="min-h-0 flex-1 overflow-y-auto p-2"
    >
      {groups.map((group) => (
        <ul
          key={group.key}
          className={cn(
            'flex flex-col gap-0.5',
            group.separated === true && 'mt-1 border-t border-hairline pt-1',
          )}
        >
          {group.items.map((item) => (
            <NavRow
              key={item.key}
              item={item}
              rail={rail}
              expanded={expanded.has(item.key)}
              lockedOpen={activeGroups.has(item.key)}
              focusFallback={item.key === fallbackKey}
              onToggle={() =>
                setExpanded((previous) => {
                  const next = new Set(previous);
                  if (next.has(item.key)) next.delete(item.key);
                  else next.add(item.key);
                  return next;
                })
              }
              {...(renderNavLink !== undefined ? { renderNavLink } : {})}
              {...(groupToggleLabel !== undefined ? { groupToggleLabel } : {})}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      ))}
    </nav>
  );
}

interface NavRowProps {
  readonly item: AdminNavItem;
  readonly rail: boolean;
  readonly expanded: boolean;
  readonly lockedOpen: boolean;
  /** This row owns the nav's single tab stop because nothing is current. */
  readonly focusFallback: boolean;
  readonly onToggle: () => void;
  readonly renderNavLink?: AppShellAdminProps['renderNavLink'];
  readonly groupToggleLabel?: (label: string) => string;
  readonly onNavigate: () => void;
}

/**
 * The four simultaneous active signals — fill, weight, a 3 px brand bar on the
 * LEADING edge, and the icon switching to brand. None of them is colour alone.
 *
 * The bar is on the leading edge rather than the "inner" edge DESIGN.md names,
 * because the trailing end of every item is already occupied by the count badge
 * or the group chevron.
 */
function itemClasses(current: boolean, rail: boolean): string {
  return cn(
    'relative flex w-full items-center rounded-control',
    'transition-colors dur-state ease-out',
    'focus-visible:outline-focus-inverse',
    rail
      ? 'min-h-16 flex-col justify-center gap-1 px-1 text-center'
      : 'min-h-nav-item gap-2.5 px-3',
    current
      ? 'bg-chassis-raised font-semibold text-white'
      : 'font-medium text-ink-inverse hover:bg-chassis-raised',
    // The marker: inset 8 px top and bottom so it reads as a marker, not a
    // border. `start-0` is logical, so it mirrors correctly in an RTL locale.
    current &&
      'before:absolute before:inset-y-2 before:start-0 before:w-[3px] before:rounded-chip before:bg-brand',
  );
}

function NavRow({
  item,
  rail,
  expanded,
  lockedOpen,
  focusFallback,
  onToggle,
  renderNavLink,
  groupToggleLabel,
  onNavigate,
}: NavRowProps) {
  const Icon = item.icon;
  const current = item.current === true;
  const hasChildren = item.children !== undefined && item.children.length > 0;
  const tabIndex = current || focusFallback ? 0 : -1;

  const body = (
    <>
      {Icon !== undefined ? (
        <Icon
          {...decorativeIcon(rail ? 'lg' : 'md')}
          className={cn('shrink-0', current && 'text-brand')}
        />
      ) : null}
      <span className={cn('min-w-0 truncate', rail ? 'text-label' : 'text-subject')}>
        {item.label}
      </span>
      {item.badge !== undefined ? (
        <Badge
          count={item.badge.count}
          label={item.badge.label}
          {...(item.badge.tone === undefined ? {} : { tone: item.badge.tone })}
          aria-label={item.badge.label}
          className={rail ? 'absolute end-1 top-1 outline-2 outline-chassis' : 'ms-auto'}
        />
      ) : null}
    </>
  );

  const link =
    item.href === undefined
      ? null
      : (renderNavLink?.(item, itemClasses(current, rail), body, { onNavigate, tabIndex }) ?? (
          <a
            href={item.href}
            data-nav-focusable=""
            tabIndex={tabIndex}
            aria-current={current ? 'page' : undefined}
            onClick={onNavigate}
            className={itemClasses(current, rail)}
          >
            {body}
          </a>
        ));

  // A leaf.
  if (!hasChildren) return <li className="min-w-0">{link}</li>;

  // A group in the rail: sub-items live in a CLICK-opened flyout.
  if (rail) {
    return (
      <li className="min-w-0">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              data-nav-focusable=""
              tabIndex={tabIndex}
              className={itemClasses(current, rail)}
            >
              {body}
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              side="right"
              align="start"
              sideOffset={4}
              className="z-50 min-w-52 rounded-panel border border-border bg-surface p-1 shadow-drawer"
            >
              {(item.children ?? []).map((child) => (
                <DropdownMenu.Item key={child.key} asChild>
                  <a
                    href={child.href ?? '#'}
                    aria-current={child.current === true ? 'page' : undefined}
                    onClick={onNavigate}
                    className={cn(
                      'flex min-h-9 items-center rounded-control px-3 text-body-compact',
                      'data-highlighted:bg-selected data-highlighted:outline-none',
                      child.current === true ? 'font-bold text-ink' : 'text-ink',
                    )}
                  >
                    {child.label}
                  </a>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </li>
    );
  }

  // A group in the sidebar or the drawer: an inline collapsible list.
  return (
    <li className="min-w-0">
      <button
        type="button"
        data-nav-focusable=""
        tabIndex={tabIndex}
        aria-expanded={expanded}
        {...(groupToggleLabel !== undefined ? { 'aria-label': groupToggleLabel(item.label) } : {})}
        onClick={() => {
          if (lockedOpen && expanded) return;
          onToggle();
        }}
        className={itemClasses(false, false)}
      >
        {body}
        <span aria-hidden="true" className="ms-auto inline-flex shrink-0">
          {expanded ? (
            <ChevronUp {...decorativeIcon('sm')} />
          ) : (
            <ChevronDown {...decorativeIcon('sm')} />
          )}
        </span>
      </button>

      {expanded ? (
        <ul className="mt-0.5 flex flex-col gap-0.5">
          {(item.children ?? []).map((child) => {
            const childCurrent = child.current === true;
            const childClass = cn(
              'relative flex min-h-nav-subitem items-center rounded-control',
              // Aligned under the parent LABEL, never under the icon.
              'ps-[42px] pe-3 text-body-compact',
              'transition-colors dur-state ease-out focus-visible:outline-focus-inverse',
              childCurrent
                ? 'bg-chassis-raised font-semibold text-white before:absolute before:inset-y-1.5 before:start-0 before:w-[3px] before:rounded-chip before:bg-brand'
                : 'font-medium text-ink-inverse hover:bg-chassis-raised',
            );
            const childBody = (
              <>
                <span className="min-w-0 truncate">{child.label}</span>
                {child.badge !== undefined ? (
                  <Badge
                    count={child.badge.count}
                    label={child.badge.label}
                    {...(child.badge.tone === undefined ? {} : { tone: child.badge.tone })}
                    aria-label={child.badge.label}
                    className="ms-auto"
                  />
                ) : null}
              </>
            );

            return (
              <li key={child.key} className="min-w-0">
                {renderNavLink?.(child, childClass, childBody, {
                  onNavigate,
                  tabIndex: childCurrent ? 0 : -1,
                }) ?? (
                  <a
                    href={child.href ?? '#'}
                    data-nav-focusable=""
                    tabIndex={childCurrent ? 0 : -1}
                    aria-current={childCurrent ? 'page' : undefined}
                    onClick={onNavigate}
                    className={childClass}
                  >
                    {childBody}
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * The account block: scope, language, PIN change, sign-out.
 *
 * A DROP-UP, because it hangs off the foot of the sidebar and a downward menu
 * would open off the bottom of the screen. It is a real `role="menu"`: `Esc`
 * closes it, focus returns to the trigger, and the arrow keys work.
 *
 * The scope switcher lives here rather than in a global top bar — the bar would
 * cost 56 px of vertical on every route to save one click on a rare action.
 */
function AccountMenu({
  name,
  initials,
  photoSrc,
  scopeLine,
  triggerLabel,
  menuLabel,
  scopeSectionLabel,
  scopes,
  onScopeChange,
  languageSectionLabel,
  locale,
  localeOptions,
  onLocaleChange,
  onSelectProfile,
  profileLabel,
  changePinLabel,
  onChangePin,
  signOutLabel,
  onSignOut,
  compact = false,
}: AccountMenuProps & { readonly compact?: boolean }) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={triggerLabel}
          className={cn(
            'flex w-full items-center gap-2.5 px-3 text-start',
            'transition-colors dur-state ease-out hover:bg-chassis-raised',
            'focus-visible:outline-focus-inverse',
            compact ? 'min-h-16 justify-center' : 'min-h-14',
          )}
        >
          <Avatar
            name={name}
            size="sm"
            surface="chassis"
            decorative
            {...(initials !== undefined ? { initials } : {})}
            {...(photoSrc !== undefined ? { src: photoSrc } : {})}
          />
          {compact ? null : (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-body-compact font-semibold text-ink-inverse">
                  {name}
                </span>
                <span className="block truncate text-label uppercase text-ink-inverse-muted">
                  {scopeLine}
                </span>
              </span>
              <ChevronUp {...decorativeIcon('sm')} className="shrink-0" />
            </>
          )}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side="top"
          align="start"
          sideOffset={6}
          aria-label={menuLabel}
          className="z-50 w-58 rounded-panel border border-border bg-surface p-1 shadow-drawer"
        >
          <DropdownMenu.Label className="px-3 py-1.5 text-label uppercase text-ink-muted">
            {scopeSectionLabel}
          </DropdownMenu.Label>
          {scopes.map((scope) => (
            <DropdownMenu.Item
              key={scope.key}
              onSelect={() => onScopeChange?.(scope.key)}
              className={cn(
                'flex min-h-10 cursor-pointer items-center gap-2 rounded-control px-3',
                'text-body-compact text-ink',
                'data-highlighted:bg-selected data-highlighted:outline-none',
                scope.current === true && 'bg-selected font-bold',
              )}
            >
              <span className="inline-flex w-4 shrink-0">
                {scope.current === true ? (
                  <Check {...decorativeIcon('sm')} className="text-action" />
                ) : null}
              </span>
              <span className="min-w-0 truncate">{scope.label}</span>
            </DropdownMenu.Item>
          ))}

          <DropdownMenu.Separator className="my-1 h-px bg-hairline" />
          <DropdownMenu.Label className="px-3 py-1.5 text-label uppercase text-ink-muted">
            {languageSectionLabel}
          </DropdownMenu.Label>
          {localeOptions.map((option) => (
            <DropdownMenu.Item
              key={option.value}
              onSelect={() => onLocaleChange(option.value)}
              className={cn(
                'flex min-h-10 cursor-pointer items-center gap-2 rounded-control px-3',
                'text-body-compact text-ink',
                'data-highlighted:bg-selected data-highlighted:outline-none',
                option.value === locale && 'bg-chassis font-bold text-ink-inverse',
              )}
            >
              <span className="inline-flex w-4 shrink-0">
                {option.value === locale ? <Check {...decorativeIcon('sm')} /> : null}
              </span>
              <span lang={option.value}>{option.label}</span>
            </DropdownMenu.Item>
          ))}

          <DropdownMenu.Separator className="my-1 h-px bg-hairline" />
          {onSelectProfile === undefined ? null : (
            <DropdownMenu.Item
              onSelect={onSelectProfile}
              className="flex min-h-10 cursor-pointer items-center gap-2 rounded-control px-3 text-body-compact text-ink data-highlighted:bg-selected data-highlighted:outline-none"
            >
              <span className="inline-flex w-4 shrink-0" />
              {profileLabel}
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item
            onSelect={onChangePin}
            className="flex min-h-10 cursor-pointer items-center gap-2 rounded-control px-3 text-body-compact text-ink data-highlighted:bg-selected data-highlighted:outline-none"
          >
            <span className="inline-flex w-4 shrink-0" />
            {changePinLabel}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={onSignOut}
            className="flex min-h-10 cursor-pointer items-center gap-2 rounded-control px-3 text-body-compact text-ink data-highlighted:bg-selected data-highlighted:outline-none"
          >
            <LogOut {...decorativeIcon('sm')} className="w-4 shrink-0" />
            {signOutLabel}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
